<template>
  <RevDrawer
    defer
    :name
    size="large"
    :title
    @open="handleDrawerOpen"
    @close="handleDrawerClose"
  >
    <template #trigger="{ open }">
      <slot name="trigger" :open />
    </template>

    <template #body>
      <RevTabs
        class="-mt-24"
        :label="i18n(translations.tabsLabel)"
        variant="fixed"
      >
        <RevTabItem
          v-for="gradeData in sortedGrades"
          :key="gradeData.gradeLabel"
          :active="activeGrade === gradeData.gradeLabel"
          :label="gradeTabLabel(gradeData.gradeLabel)"
          @click="handleTabChange(gradeData.gradeLabel)"
        />
      </RevTabs>

      <div
        v-if="activeGradeData"
        class="mt-24"
        data-test="review-photos-guidance-grade-panel"
      >
        <div
          class="grid grid-cols-1 gap-24 md:grid-cols-[1fr_1fr]"
          :class="$style.gradePanel"
        >
          <!-- Left: Carousel with thumbnails (directly in the drawer body) -->
          <div :class="$style.pictures">
            <RevCarousel
              v-model:current-index="currentPictureIndex"
              class="min-w-0 w-full overflow-hidden"
              :alternative-next="i18n(translations.alternativeNext)"
              :alternative-previous="i18n(translations.alternativePrevious)"
              :current-index-id="`${name}-carousel`"
              :thumbnails="thumbnails(activeGradeData.pictures)"
              @navigation-click="onCarouselNavigate"
              @next-button-click="onCarouselNavigate"
              @prev-button-click="onCarouselNavigate"
              @slide-impression="onSlideImpression"
            >
              <div
                v-for="picture in activeGradeData.pictures"
                :key="picture.id"
                class="relative aspect-square w-full md:max-w-[570px]"
              >
                <RevIllustration
                  alt=""
                  class="rounded-lg"
                  data-test="review-photos-guidance-carousel-picture"
                  :height="570"
                  image-class-names="h-full w-full object-cover"
                  loading="lazy"
                  :src="picture.url"
                  :width="570"
                />
                <div class="absolute inset-x-0 bottom-0 p-16">
                  <RevTag
                    class="self-start bg-overlay-low text-static-light-hi"
                    :label="
                      picture.review
                        ? i18n(translations.reviewImageTag)
                        : exampleTag
                    "
                    variant="secondary"
                  />
                </div>
              </div>
            </RevCarousel>
          </div>

          <!-- Right: Review for the current picture -->
          <div :class="$style.comment">
            <RevDivider class="my-16 md:hidden" />
            <RevCard
              v-if="!currentReview && isNewDefaultDetailsEnabled"
              class="space-y-12 p-16 md:space-y-16"
            >
              <h3 class="heading-3">
                {{ i18n(GRADE_NEW_DEFAULT_TITLE[activeGrade]) }}
              </h3>
              <p class="text-static-default-low body-2 !mt-0">
                {{ i18n(GRADE_NEW_SUBTITLE[activeGrade]) }}
              </p>
              <RevDivider class="my-0" />
              <dl class="space-y-12 pt-12">
                <div
                  class="flex items-start gap-8 text-static-default-low body-2"
                >
                  <span
                    class="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-static-info-max"
                  >
                    <IconCheckWithSparkles class="h-16 w-16" />
                  </span>
                  <div>
                    <dt class="inline">
                      <strong>{{
                        i18n(translations.newDefaultCardHowItLooksLabel)
                      }}</strong
                      >&nbsp;
                    </dt>
                    <dd class="inline">
                      {{ i18n(GRADE_NEW_HOW_IT_LOOKS[activeGrade]) }}
                    </dd>
                  </div>
                </div>
                <div
                  class="flex items-start gap-8 text-static-default-low body-2"
                >
                  <span
                    class="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-static-info-max"
                  >
                    <IconBattery class="h-16 w-16" />
                  </span>
                  <div>
                    <dt class="inline">
                      <strong>{{
                        i18n(translations.newDefaultCardBatteryLabel)
                      }}</strong
                      >&nbsp;
                    </dt>
                    <dd class="inline">
                      {{ i18n(GRADE_NEW_BATTERY[activeGrade]) }}
                    </dd>
                  </div>
                </div>
              </dl>
              <dl class="space-y-4">
                <dt class="text-static-default-low body-2">
                  <strong>{{
                    i18n(translations.newDefaultCardQualityTitle)
                  }}</strong>
                </dt>
                <dd class="text-static-default-low body-2">
                  {{ i18n(translations.newDefaultCardQualityDescription) }}
                </dd>
              </dl>
            </RevCard>
            <RevCard
              v-else-if="!currentReview"
              class="space-y-20 p-16 md:space-y-24"
            >
              <h3 class="heading-3">
                {{
                  i18n(translations.defaultCardTitle, {
                    grade: gradeTabLabel(activeGrade),
                  })
                }}
              </h3>
              <p class="text-static-default-low body-2">
                {{ gradeDefaultCardDescription() }}
              </p>
            </RevCard>
            <template v-else>
              <ReviewDisplay
                :key="currentReview.id"
                v-bind="currentReview"
                :display-seller="true"
                :force-mobile-display="true"
                :track-review-impression="true"
                :tracking-data="currentTrackingData"
              />
              <RevLink
                class="mt-16"
                variant="secondary"
                @click="handleSeeAllReviews"
              >
                {{
                  i18n(translations.seeAllReviews, {
                    grade: gradeTabLabel(activeGrade),
                  })
                }}
              </RevLink>
            </template>
          </div>
        </div>
      </div>
    </template>
  </RevDrawer>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute } from '#imports'
import { computed, nextTick, ref, watch } from 'vue'

import ReviewDisplay from '@backmarket/nuxt-layer-reviews/review-display/ReviewDisplay.vue'
import { useExperimentValue } from '@backmarket/nuxt-module-experiments/useExperimentValue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevCard } from '@ds/components/Card'
import { RevCarousel } from '@ds/components/Carousel'
import { RevDivider } from '@ds/components/Divider'
import { RevDrawer } from '@ds/components/Drawer'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { closeModal } from '@ds/components/ModalBase'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'
import { RevTag } from '@ds/components/Tag'
import { IconBattery } from '@ds/icons/IconBattery'
import { IconCheckWithSparkles } from '@ds/icons/IconCheckWithSparkles'

import type { BackboxGradeLabel } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useFetchReviewsPerGrade/useFetchReviewsPerGrade.constants'

import parentTranslations from './ReviewPhotosGuidance.translations'
import translations from './ReviewPhotosGuidanceDrawer.translations'
import type {
  FormattedReview,
  GradePictures,
  PictureItem,
} from './ReviewPhotosGuidanceDrawer.types'

const GRADE_TAB: Record<BackboxGradeLabel, typeof translations.tabPremium> = {
  PREMIUM: translations.tabPremium,
  FLAWLESS: translations.tabFlawless,
  VERY_GOOD: translations.tabVeryGood,
  ECO: translations.tabEco,
}

const GRADE_NEW_DEFAULT_TITLE: Record<
  BackboxGradeLabel,
  typeof translations.newDefaultCardTitlePremium
> = {
  PREMIUM: translations.newDefaultCardTitlePremium,
  FLAWLESS: translations.newDefaultCardTitleFlawless,
  VERY_GOOD: translations.newDefaultCardTitleVeryGood,
  ECO: translations.newDefaultCardTitleEco,
}

const GRADE_NEW_SUBTITLE: Record<
  BackboxGradeLabel,
  typeof translations.newDefaultCardSubtitlePremium
> = {
  PREMIUM: translations.newDefaultCardSubtitlePremium,
  FLAWLESS: translations.newDefaultCardSubtitleFlawless,
  VERY_GOOD: translations.newDefaultCardSubtitleVeryGood,
  ECO: translations.newDefaultCardSubtitleEco,
}

const GRADE_NEW_HOW_IT_LOOKS: Record<
  BackboxGradeLabel,
  typeof translations.newDefaultCardHowItLooksPremium
> = {
  PREMIUM: translations.newDefaultCardHowItLooksPremium,
  FLAWLESS: translations.newDefaultCardHowItLooksFlawless,
  VERY_GOOD: translations.newDefaultCardHowItLooksVeryGood,
  ECO: translations.newDefaultCardHowItLooksEco,
}

const GRADE_NEW_BATTERY: Record<
  BackboxGradeLabel,
  typeof translations.newDefaultCardBatteryPremium
> = {
  PREMIUM: translations.newDefaultCardBatteryPremium,
  FLAWLESS: translations.newDefaultCardBatteryFlawless,
  VERY_GOOD: translations.newDefaultCardBatteryVeryGood,
  ECO: translations.newDefaultCardBatteryEco,
}

const props = withDefaults(
  defineProps<{
    grades: GradePictures[]
    initialGrade: BackboxGradeLabel
    targetGrade?: BackboxGradeLabel | null
    targetPictureIndex?: number
    title: string
    name?: string
  }>(),
  {
    name: 'review-photos-guidance-drawer',
    targetGrade: null,
    targetPictureIndex: 0,
  },
)

const route = useRoute()
const i18n = useI18n()
const { trackClick, trackModal } = useTracking()

const defaultDetailsVariant = useExperimentValue(
  'experiment.reviewPhotosGuidanceDefaultDetails',
  { defaultValue: 'noVariation' },
)
const isNewDefaultDetailsEnabled =
  defaultDetailsVariant === 'withNewDefaultDetails'

const hasTrackedExperimentExposure = ref(false)
function trackExperimentExposureOnce() {
  if (hasTrackedExperimentExposure.value) return
  hasTrackedExperimentExposure.value = true
  trackClick({
    name: 'experiment_exposure',
    zone: 'review_photo_guidance',
    pageType: route.name as string,
    experiment: 'reviewPhotosGuidanceDefaultDetails',
    variant: defaultDetailsVariant,
  })
}

const activeGrade = ref<BackboxGradeLabel>(props.initialGrade)
const currentPictureIndex = ref(0)
const isInternalNavigation = ref(false)
const hasReceivedInitialImpression = ref(false)

function flagInternalNavigation() {
  isInternalNavigation.value = true
  nextTick(() => {
    isInternalNavigation.value = false
  })
}

watch(
  () => [props.targetGrade, props.targetPictureIndex] as const,
  ([gradeLabel, pictureIndex]) => {
    if (gradeLabel) {
      flagInternalNavigation()
      activeGrade.value = gradeLabel
      currentPictureIndex.value = pictureIndex
    }
  },
  { immediate: true },
)

const TAB_ORDER: BackboxGradeLabel[] = [
  'ECO',
  'VERY_GOOD',
  'FLAWLESS',
  'PREMIUM',
]

const sortedGrades = computed(() =>
  TAB_ORDER.map((label) =>
    props.grades.find((g) => g.gradeLabel === label),
  ).filter((g): g is GradePictures => g !== undefined),
)

const activeGradeData = computed<GradePictures | null>(() => {
  return props.grades.find((g) => g.gradeLabel === activeGrade.value) ?? null
})

function gradeTabLabel(gradeLabel: BackboxGradeLabel): string {
  return i18n(GRADE_TAB[gradeLabel])
}

function gradeDefaultCardDescription(): string {
  return i18n(translations.defaultCardDescription)
}

const exampleTag = computed(() =>
  i18n(parentTranslations.exampleTag, {
    grade: gradeTabLabel(activeGrade.value),
  }),
)

function thumbnails(pictures: PictureItem[]) {
  return pictures.map((picture) => ({
    src: picture.url,
    width: 100,
    height: 100,
    alt: '',
  }))
}

const currentReview = computed<FormattedReview | null>(() => {
  if (!activeGradeData.value) return null

  return (
    activeGradeData.value.pictures[currentPictureIndex.value]?.review ?? null
  )
})

const currentTrackingData = computed(() => {
  if (!currentReview.value) return undefined

  return {
    onClick: {
      reviewId: currentReview.value.id,
    },
    onImpression: {
      zone: 'review_photo_guidance',
      photosReviewsDisplayed: activeGradeData.value?.pictures.length ?? 0,
      reviewComment: Boolean(currentReview.value.comment),
      reviewPhotos: true,
      pageType: route.name as string,
      reviewId: currentReview.value.id,
    },
  }
})

function onCarouselNavigate() {
  flagInternalNavigation()
  trackClick({
    name: 'navigation',
    zone: 'review_photo_guidance',
    pageType: route.name as string,
    grade: activeGrade.value,
    pictureIndex: currentPictureIndex.value,
    totalPictures: activeGradeData.value?.pictures.length ?? 0,
    isReviewPhoto: Boolean(currentReview.value),
  })
}

function onSlideImpression({ index }: { index: number }) {
  if (!hasReceivedInitialImpression.value) {
    hasReceivedInitialImpression.value = true

    return
  }

  if (isInternalNavigation.value) {
    return
  }

  trackClick({
    name: 'navigation',
    zone: 'review_photo_guidance',
    pageType: route.name as string,
    grade: activeGrade.value,
    pictureIndex: index,
    totalPictures: activeGradeData.value?.pictures.length ?? 0,
    isReviewPhoto: Boolean(activeGradeData.value?.pictures[index]?.review),
  })
}

function handleDrawerOpen() {
  trackModal({
    status: 'opened',
    name: props.name,
    zone: 'review_photo_guidance',
    pageType: route.name as string,
    grade: activeGrade.value,
  })
  trackExperimentExposureOnce()
}

function handleDrawerClose() {
  trackModal({
    status: 'closed',
    name: props.name,
    zone: 'review_photo_guidance',
    pageType: route.name as string,
    grade: activeGrade.value,
  })
}

function handleTabChange(gradeLabel: BackboxGradeLabel) {
  flagInternalNavigation()
  activeGrade.value = gradeLabel
  currentPictureIndex.value = 0
  trackClick({
    name: 'select_grade_tab',
    zone: 'review_photo_guidance',
    pageType: route.name as string,
    grade: gradeLabel,
  })
}

async function handleSeeAllReviews() {
  trackClick({
    name: 'see_all_reviews',
    zone: 'review_photo_guidance',
    pageType: route.name as string,
    grade: activeGrade.value,
  })
  closeModal(props.name)
  await navigateTo({
    ...route,
    query: {
      ...route.query,
      backbox_grade_label__in: activeGrade.value,
    },
  })
  await nextTick()
  const target = document.getElementById('reviews')
  target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  target?.focus({ preventScroll: true })
}
</script>

<style module>
.gradePanel {
  grid-template-areas:
    'pictures'
    'comment';
}

@media only screen and (min-width: 768px) {
  .gradePanel {
    grid-template-areas: 'pictures comment';
  }
}

.pictures {
  grid-area: pictures;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.comment {
  grid-area: comment;
}
</style>
